import {AfterContentInit, ChangeDetectorRef, Component, ContentChild, EventEmitter, Inject, Output} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MxButtonModel} from '../../mx-button/components/mx-button.model';
import {MxFilterButtonsConfig} from '../../mx-grid/services/mx-filter-button.model';
import {MxGridButtonsConfig} from '../../mx-grid/services/mx-grid-button.model';
import {MxGridDisableRowFn, MxGridProps, MxGridSelectionChangedFn, MxGridService} from '../../mx-grid/services/mx-grid-customizer-event';
import {DefaultGridButtons, ReportGridButtons} from '../../mx-grid/services/mx-grid-enums';
import {AssignmentSelectionType} from '../../mx-grid/services/selection-type.model';
import {MxPreloaderService} from '../../mx-preloader/services/mx-preloader.service';
import {MxReportViewCustomizerEvent, MxReportViewInputFilterDefault} from '../../mx-report-view/services/mx-report-view-customizer-event';
import {AssignmentConfig, AssignmentResult, MxAssignmentGrid} from '../services/mx-assignment-grid';
import {MxAssignmentGridCustomizerEvent} from '../services/mx-assignment-grid-customizer-event';
import {first} from 'rxjs/operators';
import {MxAssignmentGridDialogConfig} from '../services/mx-assignment-grid-dialog-config';
import {MxCustomizable} from '../../../mx-customizer';

@Component({
    selector: 'mx-assignment-grid-dialog',
    standalone: false,
    templateUrl: './mx-assignment-grid-dialog.component.html',
    styleUrls: ['./mx-assignment-grid-dialog.component.scss']
})
export class MxAssignmentGridDialogComponent implements AfterContentInit, MxCustomizable<MxAssignmentGridCustomizerEvent> {
    @ContentChild('selectionComponent') selectionComponent: MxCustomizable<MxReportViewCustomizerEvent>;

    @Output() customize: EventEmitter<MxAssignmentGridCustomizerEvent> = new EventEmitter<MxAssignmentGridCustomizerEvent>();

    closeButton: MxButtonModel;
    okButton: MxButtonModel;

    private reportName: string;
    private assignedItems: Array<any>;
    private readonly disableRowFn: MxGridDisableRowFn;
    private readonly selectionChangedFn: MxGridSelectionChangedFn;
    public gridService: MxGridService;
    private props: MxGridProps;
    public assignmentConfig: AssignmentConfig = {
        title: '',
        keyField: 'id',
        displayField: 'name'
    };
    private assignmentResult: AssignmentResult = {
        assignedItems: [],
        assignedItemsKeys: [],
        entryFields: []
    };
    private filterDefaults: MxReportViewInputFilterDefault[];
    private selectionMode: AssignmentSelectionType;

    public preloaderName: string = 'assignment-' + Math.random().toString(36).substr(2, 9);
    ngOnInit(): void {
        this.closeButton = {
            label: 'Close',
            tooltip: 'Close',
            className: 'mxs-button--secondary',
            clickHandler: () => this.dialogRef.close()
        };
        this.okButton = {
            label: 'Ok',
            tooltip: 'Ok',
            className: 'mxs-button--primary',
            clickHandler: this.assign
        };
    }
    ngAfterContentInit(): void {
        this.selectionComponent.customize.pipe(first())
            .subscribe(
                (event) => {
                    if (event instanceof MxReportViewCustomizerEvent) {
                        event.customizePreloaderConfig({name: this.preloaderName})
                            .customizeFilterButtons(new MxFilterButtonsConfig([
                                ['export', {hidden: true}]]))
                            .customizeFilterDefaults(this.filterDefaults);
                        if (this.reportName) {
                            event.customizeReportName(this.reportName);
                        }
                    }
                    let e: MxAssignmentGridCustomizerEvent = new MxAssignmentGridCustomizerEvent;

                    e.gridCustomizerEvent = event;
                    e.gridCustomizerEvent.customizeProps({
                        ...this.props,
                        ...{
                            noPagination: false,
                            paginationLimit: 10,
                            selectionMode: this.selectionMode,
                            isGoToItemDetailsAllowed: false,
                            saveGridState: false,
                            useQueryParam: false
                        }
                    });
                    e.customizeAssignmentDialog(this.assignmentConfig);
                    if (this.assignmentConfig.extractor) {
                        e.gridCustomizerEvent.customizeExtractId(this.assignmentConfig.extractor);
                    }
                    if (this.disableRowFn) {
                        e.gridCustomizerEvent.customizeDisableRow(this.disableRowFn);
                    }
                    if (this.selectionChangedFn) {
                        e.gridCustomizerEvent.customizeSelectionChangedFn(this.selectionChangedFn);
                    }

                    this.customize.emit(e);
                    let defaultButtons = Object.keys(DefaultGridButtons);
                    e.gridCustomizerEvent.actions.forEach(
                        (curr) => curr.forEach((_item, name) => {
                            if (defaultButtons.indexOf(name) === -1) {
                                defaultButtons.push(name);
                            }
                        }));
                    e.gridCustomizerEvent.customizeActions(
                        MxGridButtonsConfig.multiConfig(defaultButtons
                            .filter(value => Object.keys(ReportGridButtons).indexOf(value) === -1), {hidden: true}));
                    this.assignmentConfig = e.assignmentConfig;

                    e.gridCustomizerEvent.onSetUp((service) => {
                        this.gridService = service;
                        let initialSelectedKeys: Array<number | string> = this.assignedItems.map(
                            item => {
                                if (this.selectionMode === 'multi') {
                                    return e.extractId ? e.extractId(item) : this.gridService.collection.extractId(item);
                                } else {
                                    // if 'single', item is an entryField, which is key/value
                                    return item.key;
                                }
                            });
                        this.gridService.collection.setInitialSelectedKeys(initialSelectedKeys);
                        this.assignedItems = [];

                        this.cdRef.detectChanges();
                    });
                }
            );
    }

    constructor(private cdRef: ChangeDetectorRef,
                private dialogRef: MatDialogRef<MxAssignmentGridDialogComponent>,
        private assignmentGridComponent: MxAssignmentGrid,
                private preloader: MxPreloaderService,
                @Inject(MAT_DIALOG_DATA) private config: MxAssignmentGridDialogConfig) {
        this.preloader.show({type: 'RoundSpinner', name: this.preloaderName});
        this.reportName = this.config.reportName;
        this.assignedItems = (this.config.assignedItems) ? [...this.config.assignedItems] : [];
        this.disableRowFn = this.config.disableRowFunc;
        this.selectionChangedFn = this.config.selectionChangedFunc;
        this.filterDefaults = this.config.filterDefaults;
        this.selectionMode = this.config.selectionMode === 'single' ? 'single' : 'multi';
        this.props = this.config.props;
        if (this.config.assignmentConfig) {
            this.assignmentConfig = this.config.assignmentConfig;
        }
    }

    assign = () => {
        Object.assign(this.assignedItems, this.gridService.collection.fullSelection);
        let assignedItemsKeys = [...this.assignedItems.map(item => this.gridService.collection.extractId(item)),
            ...this.gridService.collection.notSelectedInitKeys];
        this.assignmentResult = {
            assignedItems: this.assignedItems,
            assignedItemsKeys,
            assignmentConfig: this.assignmentConfig,
            entryFields: this.assignedItems.map(i => this.assignmentGridComponent.mapAssigmentToField(i, this.assignmentConfig))
        };
        this.dialogRef.close(this.assignmentResult);
    }

}
